import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAdjustStock } from '../../hooks/useInventory';
import { Product, AdjustStockDto } from '../../services/inventory.service';
import { Modal } from '../ui/Modal';
import {
  Field,
  FormActions,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import ProductStockSummary from './ProductStockSummary';

interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

const adjustmentReasons = [
  { value: 'adjustment_in', label: 'Adjustment In' },
  { value: 'adjustment_out', label: 'Adjustment Out' },
  { value: 'damage', label: 'Damage' },
  { value: 'expired', label: 'Expired' },
  { value: 'lost', label: 'Lost' },
  { value: 'found', label: 'Found' },
  { value: 'recount', label: 'Recount' },
  { value: 'quality_issue', label: 'Quality Issue' },
];

const emptyForm: AdjustStockDto = {
  adjustmentQuantity: 0,
  reason: 'adjustment_in',
  unitCost: 0,
  notes: '',
  location: '',
  referenceNumber: '',
};

const StockAdjustmentModal: React.FC<StockAdjustmentModalProps> = ({ isOpen, onClose, product }) => {
  const [formData, setFormData] = useState<AdjustStockDto>(emptyForm);
  const adjustStockMutation = useAdjustStock();

  // Start from a clean form every time the dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData(emptyForm);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;

    try {
      await adjustStockMutation.mutateAsync({
        id: product.id,
        data: {
          ...formData,
          unitCost: formData.unitCost || undefined,
          notes: formData.notes || undefined,
          location: formData.location || undefined,
          referenceNumber: formData.referenceNumber || undefined,
        },
      });
      onClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  if (!product) return null;

  const newStock = Number(product.currentStock) + formData.adjustmentQuantity;
  const isNegative = newStock < 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Adjust Stock"
      description="Correct the stock level up or down, with a reason."
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <ProductStockSummary product={product} newStock={newStock} />

        {isNegative && (
          <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
            <p className="text-sm text-red-700">
              This adjustment would take stock below zero. Reduce the quantity to continue.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Adjustment Quantity"
            htmlFor="adjust-stock-quantity"
            required
            hint="Positive to increase stock, negative to decrease."
          >
            <input
              type="number"
              id="adjust-stock-quantity"
              step="0.0001"
              required
              value={formData.adjustmentQuantity || ''}
              onChange={(e) =>
                setFormData({ ...formData, adjustmentQuantity: Math.round((parseFloat(e.target.value) || 0) * 10000) / 10000 })
              }
              className={inputClass}
              placeholder="e.g. 10 or -5"
            />
          </Field>

          <Field label="Reason" htmlFor="adjust-stock-reason" required>
            <select
              id="adjust-stock-reason"
              required
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className={inputClass}
            >
              {adjustmentReasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Unit Cost (₹)" htmlFor="adjust-stock-unit-cost">
            <input
              type="number"
              id="adjust-stock-unit-cost"
              min="0"
              step="0.01"
              value={formData.unitCost || ''}
              onChange={(e) => setFormData({ ...formData, unitCost: parseFloat(e.target.value) || 0 })}
              className={inputClass}
              placeholder="0.00"
            />
          </Field>

          <Field label="Location" htmlFor="adjust-stock-location">
            <input
              type="text"
              id="adjust-stock-location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className={inputClass}
              placeholder="e.g. Warehouse A, Bin B-12"
            />
          </Field>

          <Field label="Reference Number" htmlFor="adjust-stock-reference" className="sm:col-span-2">
            <input
              type="text"
              id="adjust-stock-reference"
              value={formData.referenceNumber}
              onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
              className={inputClass}
              placeholder="e.g. ADJ-2024-001"
            />
          </Field>

          <Field label="Notes" htmlFor="adjust-stock-notes" required className="sm:col-span-2">
            <textarea
              id="adjust-stock-notes"
              rows={3}
              required
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className={inputClass}
              placeholder="Explain the reason for this adjustment..."
            />
          </Field>
        </div>

        <FormActions>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              adjustStockMutation.isPending ||
              formData.adjustmentQuantity === 0 ||
              isNegative ||
              !formData.notes?.trim()
            }
            className={primaryButtonClass}
          >
            {adjustStockMutation.isPending ? 'Adjusting...' : 'Adjust Stock'}
          </button>
        </FormActions>
      </form>
    </Modal>
  );
};

export default StockAdjustmentModal;
